import React from 'react';
import {
  X,
  MapPin,
  Phone,
  User,
  Package,
  Clock,
  FileText,
  Store,
} from 'lucide-react';

function PickupDetailModal({ pickup, onClose }) {
  function getStatusColor(status) {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'encamino':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'recogido':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'cancelado':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En Camino',
      recogido: 'Recogido',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-3 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-extrabold text-[#67A43C] font-montserrat">
              Detalle de Recojo
            </h2>
            <p className="text-xs text-slate-600 font-montserrat mt-0.5">
              ID: {pickup.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Status Badge */}
          <div className="flex justify-center">
            <span
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 font-montserrat ${getStatusColor(
                pickup.status
              )}`}
            >
              {getStatusLabel(pickup.status)}
            </span>
          </div>

          {/* Pickup Details Grid */}
          <div className="grid grid-cols-1 gap-3">
            {/* Customer Name */}
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <User className="w-3.5 h-3.5" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Cliente
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {pickup.customer_name || 'N/A'}
              </p>
            </div>

            {/* Store Name */}
            {pickup.store_name && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Store className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Tienda
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {pickup.store_name}
                </p>
              </div>
            )}

            {/* Phone */}
            {pickup.customer_phone && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Phone className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Teléfono
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {pickup.customer_phone}
                </p>
              </div>
            )}

            {/* Address */}
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <MapPin className="w-3.5 h-3.5" />
                <span className="text-xs font-medium font-montserrat uppercase">
                  Dirección de Recojo
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-900 font-montserrat">
                {pickup.address || 'N/A'}
              </p>
            </div>

            {/* Package Count */}
            {pickup.package_count && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Package className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Cantidad de Paquetes
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {pickup.package_count}
                </p>
              </div>
            )}

            {/* Sequence */}
            {pickup.sequence && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Secuencia
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  #{pickup.sequence}
                </p>
              </div>
            )}

            {/* Pickup Date */}
            {pickup.recojo_date && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Fecha de Recojo
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat">
                  {new Date(pickup.recojo_date).toLocaleDateString('es-PE', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            )}

            {/* Notes */}
            {pickup.notes && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <FileText className="w-3.5 h-3.5" />
                  <span className="text-xs font-medium font-montserrat uppercase">
                    Notas
                  </span>
                </div>
                <p className="text-sm font-semibold text-slate-900 font-montserrat break-words">
                  {pickup.notes}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#67A43C] hover:bg-[#67A43C]/90 text-white text-sm font-semibold rounded-lg transition-colors font-montserrat"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default PickupDetailModal;